import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
    Button, 
    Box, 
    Card, 
    Typography, 
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    TableSortLabel,
    Paper,
    TextField,
    IconButton,
    Toolbar,
    InputAdornment,
    Chip} from '@mui/material';
import { 
    Add, 
    Edit, 
    Delete,
    Search,
    CalendarToday
} from '@mui/icons-material';
import { getImageUrl } from '../../../../services/api';
import offerApi from '../../../../services/offerApi'; 
import Swal from 'sweetalert2';

const OfferIndex = () => {
    const [offers, setOffers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [order, setOrder] = useState('asc');
    const [orderBy, setOrderBy] = useState('id');
    const [searchTerm, setSearchTerm] = useState('');
    const navigate = useNavigate();

    useEffect(() => {
        const fetchOffers = async () => {
            try {
                const response = await offerApi.getAll();
                
                const offersWithFullUrls = response.map(offer => ({
                    ...offer,
                    imageUrl: getImageUrl(offer.imageUrl),
                    validFrom: new Date(offer.validFrom),
                    validUntil: new Date(offer.validUntil)
                }));
                
                setOffers(offersWithFullUrls);
            } catch (error) {
                console.error('Fetch error:', error);
            } finally {
                setLoading(false);
            }
        };
    
        fetchOffers();
    }, []);

    const handleDelete = async (id) => {
        const offerToDelete = offers.find(offer => offer.id === id);
        
        const confirm = await Swal.fire({
            title: 'Confirm Delete',
            text: `Are you sure you want to delete "${offerToDelete?.title}"? This action cannot be undone.`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it!',
            cancelButtonText: 'Cancel'
        });
    
        if (!confirm.isConfirmed) return;
    
        try {
            await offerApi.delete(id);
            setOffers(offers.filter(offer => offer.id !== id));
            
            Swal.fire(
                'Deleted!',
                `Offer "${offerToDelete?.title}" has been deleted.`,
                'success'
            );
        } catch (error) {
            console.error('Error deleting offer:', error);
            Swal.fire(
                'Error!',
                error.response?.data?.error || 'Failed to delete offer',
                'error'
            );
        }
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleRequestSort = (property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const handleSearch = (event) => {
        setSearchTerm(event.target.value);
        setPage(0);
    };

    const descendingComparator = (a, b, orderBy) => {
        if (b[orderBy] < a[orderBy]) return -1;
        if (b[orderBy] > a[orderBy]) return 1;
        return 0;
    };

    const getComparator = (order, orderBy) => {
        return order === 'desc'
            ? (a, b) => descendingComparator(a, b, orderBy)
            : (a, b) => -descendingComparator(a, b, orderBy);
    };

    const stableSort = (array, comparator) => {
        const stabilizedThis = array.map((el, index) => [el, index]);
        stabilizedThis.sort((a, b) => {
            const order = comparator(a[0], b[0]);
            if (order !== 0) return order;
            return a[1] - b[1];
        });
        return stabilizedThis.map((el) => el[0]);
    };

    const filteredOffers = offers.filter(offer => 
        offer?.title?.toLowerCase().includes(searchTerm?.toLowerCase()) ||
        offer?.description?.toLowerCase().includes(searchTerm?.toLowerCase()) ||
        offer?.discount?.toLowerCase().includes(searchTerm?.toLowerCase())
    );

    const sortedOffers = stableSort(filteredOffers, getComparator(order, orderBy));
    const paginatedOffers = sortedOffers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    const OfferImagePreview = ({ imageUrl, alt }) => {
        const [imgError, setImgError] = useState(false);
        const [isLoading, setIsLoading] = useState(true);
    
        useEffect(() => {
            if (!imageUrl) {
                setImgError(true);
                setIsLoading(false);
                return;
            }
    
            const img = new Image();
            img.src = imageUrl;
            
            img.onload = () => {
                setIsLoading(false);
                setImgError(false);
            };
            
            img.onerror = () => {
                setIsLoading(false);
                setImgError(true);
            };
        }, [imageUrl]);
    
        if (isLoading) {
            return (
                <Box sx={{ 
                    width: 80, 
                    height: 50,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#f5f5f5',
                    borderRadius: 1
                }}>
                    <Typography variant="caption" color="textSecondary">
                        Loading...
                    </Typography>
                </Box>
            );
        }
    
        if (imgError || !imageUrl) {
            return (
                <Box sx={{ 
                    width: 80, 
                    height: 50,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#f5f5f5',
                    borderRadius: 1
                }}>
                    <Typography variant="caption" color="textSecondary">
                        No Image
                    </Typography>
                </Box>
            );
        }

        return (
            <Box sx={{ position: 'relative', width: 80, height: 50 }}>
                <img
                    src={imageUrl}
                    alt={alt}
                    style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        borderRadius: 4
                    }}
                />
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0,0,0,0.5)',
                        opacity: 0,
                        transition: 'opacity 0.3s',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: 1,
                        cursor: 'pointer',
                        '&:hover': { opacity: 1 }
                    }}
                    onClick={() => window.open(imageUrl, '_blank')}
                >
                    <Typography variant="caption" color="white">
                        View
                    </Typography>
                </Box>
            </Box>
        );
    };

    const formatDate = (date) => {
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const isOfferActive = (validFrom, validUntil) => {
        const now = new Date();
        return now >= validFrom && now <= validUntil;
    };

    const columns = [
        { 
            id: 'serialNo', 
            label: '#', 
            width: '80px', 
            sortable: false,
            format: (value, row, page, rowsPerPage, index) => (
                <Typography variant="body2">
                    {(page * rowsPerPage) + index + 1}
                </Typography>
            )
        },
        { id: 'title', label: 'Offer Title', width: '200px', sortable: true },
        { 
            id: 'discount', 
            label: 'Discount', 
            width: '120px',
            sortable: true,
            format: (value) => (
                <Chip 
                    label={value} 
                    color="primary" 
                    size="small"
                    sx={{ fontWeight: 600 }}
                />
            )
        },
        { 
            id: 'description', 
            label: 'Description', 
            width: '250px',
            sortable: false,
            format: (value) => (
                <Typography variant="body2" noWrap>
                    {value || '-'}
                </Typography>
            )
        },
        { 
            id: 'validity', 
            label: 'Validity', 
            width: '200px',
            sortable: false,
            format: (value, row) => (
                <Stack direction="column" spacing={0.5}>
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                        <CalendarToday fontSize="small" color="action" />
                        <Typography variant="caption">
                            {formatDate(row.validFrom)} - {formatDate(row.validUntil)}
                        </Typography>
                    </Stack>
                    <Chip 
                        label={isOfferActive(row.validFrom, row.validUntil) ? 'Active' : 'Expired'} 
                        size="small"
                        color={isOfferActive(row.validFrom, row.validUntil) ? 'success' : 'error'}
                        variant="outlined"
                    />
                </Stack>
            )
        },
        { 
            id: 'imagePreview', 
            label: 'Image', 
            width: '120px',
            align: 'center',
            sortable: false,
            format: (value, row) => <OfferImagePreview imageUrl={row.imageUrl} alt={row.title} />
        },
        { 
            id: 'actions', 
            label: 'Actions', 
            width: '120px',
            align: 'center',
            sortable: false,
            format: (value, row) => (
                <>
                    <IconButton
                        aria-label="edit"
                        onClick={() => navigate(`/admin/offers/${row.id}`)}
                    >
                        <Edit fontSize="small" />
                    </IconButton>
                    <IconButton
                        aria-label="delete"
                        onClick={() => handleDelete(row.id)}
                    >
                        <Delete fontSize="small" />
                    </IconButton>
                </>
            )
        }
    ];

    return (
        <Box sx={{ width: '100%' }}>
            <Stack 
                direction="row" 
                alignItems="center" 
                justifyContent="space-between" 
                mb={3}
                sx={{
                    flexDirection: { xs: 'column', sm: 'row' },
                    alignItems: { xs: 'flex-start', sm: 'center' },
                    gap: { xs: 2, sm: 0 }
                }}
            >
                <Typography 
                    variant="h4" 
                    sx={{ 
                        fontSize: { xs: '1.5rem', sm: '1.7rem' },
                        fontWeight: 600
                    }}
                >
                    Offer Management
                </Typography>
                <Button
                    variant="contained"
                    color="primary"
                    startIcon={<Add />}
                    onClick={() => navigate('/admin/offers/new')}
                    sx={{ 
                        boxShadow: 'none',
                        textTransform: 'capitalize',
                        borderRadius: '6px',
                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                        padding: { xs: '6px 12px', sm: '8px 16px' }
                    }}
                >
                    New Offer
                </Button>
            </Stack>

            <Card sx={{ 
                p: 2,
                boxShadow: 'rgba(145, 158, 171, 0.2) 0px 0px 2px 0px, rgba(145, 158, 171, 0.12) 0px 12px 24px -4px',
                borderRadius: '12px'
            }}>
                <Paper sx={{ width: '100%', mb: 2 }}>
                    <Toolbar
                        sx={{
                            pl: { sm: 2 },
                            pr: { xs: 1, sm: 1 },
                        }}
                    >
                        <TextField
                            variant="outlined"
                            size="small"
                            placeholder="Search offers..."
                            value={searchTerm}
                            onChange={handleSearch}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Search />
                                    </InputAdornment>
                                ),
                            }}
                            sx={{
                                width: '300px',
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '6px'
                                }
                            }}
                        />
                    </Toolbar>
                    
                    <TableContainer>
                        <Table
                            sx={{ minWidth: 750 }}
                            aria-labelledby="tableTitle"
                            size={'medium'}
                        >
                            <TableHead>
                                <TableRow>
                                    {columns.map((column) => (
                                        <TableCell
                                            key={column.id}
                                            align={column.align || 'left'}
                                            width={column.width}
                                            sortDirection={orderBy === column.id ? order : false}
                                            sx={{
                                                fontWeight: 600,
                                                color: 'text.secondary',
                                                backgroundColor: 'background.paper',
                                                borderBottom: '1px solid',
                                                borderColor: 'divider'
                                            }}
                                        >
                                            {column.sortable ? (
                                                <TableSortLabel
                                                    active={orderBy === column.id}
                                                    direction={orderBy === column.id ? order : 'asc'}
                                                    onClick={() => handleRequestSort(column.id)}
                                                >
                                                    {column.label}
                                                </TableSortLabel>
                                            ) : (
                                                column.label
                                            )}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {loading ? (
                                    <TableRow>
                                        <TableCell colSpan={columns.length} align="center">
                                            Loading...
                                        </TableCell>
                                    </TableRow>
                                ) : paginatedOffers.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={columns.length} align="center">
                                            No offers found
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    paginatedOffers.map((row, index) => (
                                        <TableRow
                                            hover
                                            key={row.id}
                                            sx={{
                                                '& .MuiTableCell-root': {
                                                    borderBottom: '1px dashed',
                                                    borderColor: 'divider'
                                                }
                                            }}
                                        >
                                            {columns.map((column) => {
                                                const value = row[column.id];
                                                return (
                                                    <TableCell 
                                                        key={column.id}
                                                        align={column.align}
                                                    >
                                                        {column.format 
                                                            ? column.format(value, row, page, rowsPerPage, index)
                                                            : (
                                                                <Typography 
                                                                    variant="body2"
                                                                    noWrap
                                                                >
                                                                    {value || '-'}
                                                                </Typography>
                                                            )
                                                        }
                                                    </TableCell>
                                                );
                                            })}
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        rowsPerPageOptions={[5, 10, 20, 50]}
                        component="div"
                        count={filteredOffers.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                </Paper>
            </Card>
        </Box>
    );
};

export default OfferIndex;